import { createReducer, on } from '@ngrx/store';
import { User, UserFilter, PagedResult, Role } from '../../core';
import { UsersPageActions, UsersAPIActions } from './users.actions';

export interface UsersState {
  users: PagedResult<User> | null;
  loading: boolean;
  error: string | null;
  currentFilter: UserFilter;
  roles: Role[];
  rolesLoading: boolean;
  sortBy: string;
  sortOrder: 'asc' | 'desc' | '';
}

const defaultFilter: UserFilter = {
  page: 1,
  pageSize: 25,
  sortBy: 'lastName',
  sortOrder: 'asc',
};

export const initialState: UsersState = {
  users: null,
  loading: false,
  error: null,
  currentFilter: defaultFilter,
  roles: [],
  rolesLoading: false,
  sortBy: '',
  sortOrder: '',
};

export const usersReducer = createReducer(
  initialState,

  // Load Users
  on(UsersPageActions.loadUsers, (state, { filter }) => ({
    ...state,
    loading: true,
    error: null,
    currentFilter: { ...state.currentFilter, ...filter },
  })),

  on(UsersPageActions.search, (state, { searchTerm }) => ({
    ...state,
    loading: true,
    error: null,
    currentFilter: { ...state.currentFilter, search: searchTerm, page: 1 },
  })),

  on(UsersPageActions.sort, (state, { sortField }) => {
    const currentSortBy = state.sortBy;
    const newSortBy = sortField;
    let newSortOrder: 'asc' | 'desc' = 'asc';

    if (currentSortBy === sortField) {
      newSortOrder = state.sortOrder === 'asc' ? 'desc' : 'asc';
    }

    return {
      ...state,
      loading: true,
      error: null,
      sortBy: newSortBy,
      sortOrder: newSortOrder,
      currentFilter: {
        ...state.currentFilter,
        sortBy: newSortBy,
        sortOrder: newSortOrder,
        page: 1,
      },
    };
  }),

  on(UsersPageActions.changePage, (state, { page }) => ({
    ...state,
    loading: true,
    error: null,
    currentFilter: { ...state.currentFilter, page },
  })),

  on(UsersPageActions.changePageSize, (state, { pageSize }) => ({
    ...state,
    loading: true,
    error: null,
    currentFilter: { ...state.currentFilter, pageSize, page: 1 },
  })),

  on(UsersPageActions.filterByStatus, (state, { status }) => ({
    ...state,
    loading: true,
    error: null,
    currentFilter: { ...state.currentFilter, status, page: 1 },
  })),

  on(UsersPageActions.filterByRole, (state, { roleName }) => ({
    ...state,
    loading: true,
    error: null,
    currentFilter: { ...state.currentFilter, roleName, page: 1 },
  })),

  on(UsersPageActions.clearFilters, state => ({
    ...state,
    loading: true,
    error: null,
    currentFilter: defaultFilter,
  })),

  on(UsersPageActions.refresh, state => ({
    ...state,
    loading: true,
    error: null,
  })),

  // API responses
  on(UsersAPIActions.loadUsersSuccess, (state, { result }) => ({
    ...state,
    users: result,
    loading: false,
    error: null,
  })),

  on(UsersAPIActions.loadUsersFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Roles
  on(UsersPageActions.loadRoles, state => ({
    ...state,
    rolesLoading: true,
  })),

  on(UsersAPIActions.loadRolesSuccess, (state, { roles }) => ({
    ...state,
    roles,
    rolesLoading: false,
  })),

  on(UsersAPIActions.loadRolesFailure, state => ({
    ...state,
    rolesLoading: false,
  }))
);
